import { ReactNode } from "react";
import styled from "styled-components";

const Container = styled.div`
  font-size: 0.875rem;
  line-height: 1.5;
  color: #30313d;

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin: 1em 0 0.5em;
    line-height: 1.3;
    font-weight: 600;
  }

  h1 {
    font-size: 1.5rem;
  }
  h2 {
    font-size: 1.25rem;
  }
  h3 {
    font-size: 1.125rem;
  }
  h4,
  h5,
  h6 {
    font-size: 1rem;
  }

  p {
    margin: 0 0 0.75em;
  }

  ul,
  ol {
    margin: 0 0 0.75em;
    padding-left: 1.5em;
  }

  li {
    margin-bottom: 0.25em;
  }

  a {
    color: #7c4dff;
    text-decoration: underline;
  }

  strong {
    font-weight: 600;
  }
`;

const HEADING_RE = /^(#{1,6})\s+(.*)$/;
const UL_RE = /^\s*[-*]\s+/;
const OL_RE = /^\s*\d+\.\s+/;

const SAFE_URL_RE = /^(https?:|mailto:|tel:|\/|#)/i;
const ESCAPE_RE = /\\(\\|n|r|t|"|'|\[|\]|\(|\))/g;

function findMatchingBracket(text: string, openIndex: number): number {
  let depth = 1;
  let j = openIndex + 1;
  while (j < text.length) {
    const c = text.charAt(j);
    if (c === "\\" && j + 1 < text.length) {
      j += 2;
    } else {
      if (c === "[") {
        depth += 1;
      } else if (c === "]") {
        depth -= 1;
        if (depth === 0) return j;
      }
      j += 1;
    }
  }
  return -1;
}

function safeHref(url: string): string {
  return SAFE_URL_RE.test(url) ? url : "#";
}

function unescape(source: string): string {
  return source.replace(ESCAPE_RE, (_, ch) => {
    if (ch === "n") return "\n";
    if (ch === "r") return "\r";
    if (ch === "t") return "\t";
    return ch;
  });
}

function parseInline(text: string, keyPrefix: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  let buffer = "";
  let keyIdx = 0;
  let i = 0;

  const flushBuffer = () => {
    if (buffer) {
      nodes.push(buffer);
      buffer = "";
    }
  };

  while (i < text.length) {
    const ch = text.charAt(i);
    const next = text.charAt(i + 1);
    let handled = false;

    if (ch === "*" && next === "*") {
      const end = text.indexOf("**", i + 2);
      if (end !== -1) {
        flushBuffer();
        const inner = text.slice(i + 2, end);
        nodes.push(
          <strong key={`${keyPrefix}-b${keyIdx}`}>
            {parseInline(inner, `${keyPrefix}-b${keyIdx}`)}
          </strong>
        );
        keyIdx += 1;
        i = end + 2;
        handled = true;
      }
    }

    if (!handled && (ch === "*" || ch === "_")) {
      const end = text.indexOf(ch, i + 1);
      if (end !== -1 && end !== i + 1) {
        flushBuffer();
        const inner = text.slice(i + 1, end);
        nodes.push(
          <em key={`${keyPrefix}-i${keyIdx}`}>
            {parseInline(inner, `${keyPrefix}-i${keyIdx}`)}
          </em>
        );
        keyIdx += 1;
        i = end + 1;
        handled = true;
      }
    }

    if (!handled && ch === "[") {
      const closeBracket = findMatchingBracket(text, i);
      if (closeBracket !== -1 && text.charAt(closeBracket + 1) === "(") {
        const closeParen = text.indexOf(")", closeBracket + 2);
        if (closeParen !== -1) {
          const label = text.slice(i + 1, closeBracket);
          const url = text.slice(closeBracket + 2, closeParen);
          flushBuffer();
          nodes.push(
            <a
              key={`${keyPrefix}-a${keyIdx}`}
              href={safeHref(url)}
              target="_blank"
              rel="noopener noreferrer"
            >
              {parseInline(label, `${keyPrefix}-a${keyIdx}`)}
            </a>
          );
          keyIdx += 1;
          i = closeParen + 1;
          handled = true;
        }
      }
    }

    if (!handled && ch === "\n") {
      flushBuffer();
      nodes.push(<br key={`${keyPrefix}-br${keyIdx}`} />);
      keyIdx += 1;
      i += 1;
      handled = true;
    }

    if (!handled) {
      buffer += ch;
      i += 1;
    }
  }

  flushBuffer();
  return nodes;
}

function parseBlocks(source: string): ReactNode[] {
  const normalized = unescape(source).replace(/\r\n/g, "\n");
  const lines = normalized.split("\n");
  const blocks: ReactNode[] = [];
  let i = 0;
  let key = 0;

  while (i < lines.length) {
    const line = lines[i];
    const heading = line.trim() ? HEADING_RE.exec(line) : null;

    if (!line.trim()) {
      i += 1;
    } else if (heading) {
      const level = Math.min(heading[1].length, 6);
      const content = heading[2];
      const headingKey = `h-${key}`;
      key += 1;
      const inner = parseInline(content, headingKey);
      if (level === 1) blocks.push(<h1 key={headingKey}>{inner}</h1>);
      else if (level === 2) blocks.push(<h2 key={headingKey}>{inner}</h2>);
      else if (level === 3) blocks.push(<h3 key={headingKey}>{inner}</h3>);
      else if (level === 4) blocks.push(<h4 key={headingKey}>{inner}</h4>);
      else if (level === 5) blocks.push(<h5 key={headingKey}>{inner}</h5>);
      else blocks.push(<h6 key={headingKey}>{inner}</h6>);
      i += 1;
    } else if (UL_RE.test(line)) {
      const items: ReactNode[] = [];
      while (i < lines.length && UL_RE.test(lines[i])) {
        const itemText = lines[i].replace(UL_RE, "");
        items.push(<li key={`li-${i}`}>{parseInline(itemText, `li-${i}`)}</li>);
        i += 1;
      }
      blocks.push(<ul key={`ul-${key}`}>{items}</ul>);
      key += 1;
    } else if (OL_RE.test(line)) {
      const items: ReactNode[] = [];
      while (i < lines.length && OL_RE.test(lines[i])) {
        const itemText = lines[i].replace(OL_RE, "");
        items.push(<li key={`li-${i}`}>{parseInline(itemText, `li-${i}`)}</li>);
        i += 1;
      }
      blocks.push(<ol key={`ol-${key}`}>{items}</ol>);
      key += 1;
    } else {
      const paragraphLines: string[] = [];
      while (
        i < lines.length &&
        lines[i].trim() &&
        !HEADING_RE.test(lines[i]) &&
        !UL_RE.test(lines[i]) &&
        !OL_RE.test(lines[i])
      ) {
        paragraphLines.push(lines[i]);
        i += 1;
      }
      const paragraphKey = `p-${key}`;
      key += 1;
      blocks.push(
        <p key={paragraphKey}>
          {parseInline(paragraphLines.join("\n"), paragraphKey)}
        </p>
      );
    }
  }

  return blocks;
}

interface IMarkdownProps {
  children: string;
  className?: string;
}

export function Markdown({ children, className }: IMarkdownProps) {
  return <Container className={className}>{parseBlocks(children)}</Container>;
}
